import { Component, OnInit, Type } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';

interface FileInputProps extends FormlyFieldProps {
  accept?: string;
}

export interface FileInputFieldConfig extends FormlyFieldConfig<FileInputProps> {
  type: 'file' | Type<FileInputComponent>;
}

@Component({
  selector: 'vectronic-formly-file-input',
  imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    FormlyModule,
    MatInputModule,
  ],
  template: `
    <input
      type="file"
      class="file-input"
      (change)="onFileSelected($event)"
      #fileUpload
      [accept]="props?.accept"
    />
    <div class="file-upload">
      <mat-form-field>
        <mat-label>{{ props.label }}</mat-label>
        <input
          [id]="id"
          [name]="field.name"
          matInput
          type="text"
          [required]="props?.required"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [tabIndex]="props?.tabindex"
          (click)="fileUpload.click()"
          readonly
        />
        <mat-icon matSuffix (click)="fileUpload.click()">insert_drive_file</mat-icon>
        <button mat-icon-button matSuffix (click)="formControl.setValue(null)">
          <mat-icon>close</mat-icon>
        </button>
        @if (showError) {
          <mat-error>
            <formly-validation-message [field]="field"></formly-validation-message>
          </mat-error>
        }
      </mat-form-field>
    </div>
  `,
  styles: [
    `
      .file-input {
        display: none;
      }

      mat-form-field {
        display: block;
      }

      mat-icon {
        color: #44474e;
      }
    `,
  ],
})
export class FileInputComponent
  extends FieldType<FieldTypeConfig<FileInputProps>>
  implements OnInit
{
  matcher = new CustomErrorStateMatcher();

  ngOnInit(): void {
    this.formControl.addValidators(this.acceptValidator);
  }

  private acceptValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (this.props?.accept) {
      const file: string = control.value;
      return file && !file.toLowerCase().endsWith(this.props.accept.toLowerCase())
        ? { accept: { message: 'Incorrect file extension' } }
        : null;
    }
    return null;
  };

  get buttonLabel() {
    return `${this.props?.label || 'Choose file'} ${this.props?.required ? '*' : ''}`;
  }

  onFileSelected(event: Event) {
    const file: File = (event.target as HTMLInputElement).files[0];
    if (file) {
      this.formControl.setValue(file.name);
    }
  }
}
